import { useLeadsCRUDActions } from '@mm/state/actions/LeadsCRUDActions';
import {
  LeadsSearchKeyType,
  LeadsSearchQuery,
} from '@mm/state/interfaces/leads';
import { selectLeadsSearchFilterAttributes } from '@mm/state/selectors/leadsSearchSelectors';
import { debounce, uniqueId } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from '../../../../../../common/hooks/use-previous';

export const useLeadSearchFilters = ({
  filterParams,
  fetchAttributes = false,
}: {
  fetchAttributes?: boolean;
  filterParams: LeadsSearchQuery;
}) => {
  const [busy, setBusy] = useState(false);
  const attributes = useSelector(selectLeadsSearchFilterAttributes);
  const { retrieveAttributes, searchLeads, resetLeads } = useLeadsCRUDActions();

  const mapAttributeValuesToCheckboxItems = (key: LeadsSearchKeyType) => {
    return attributes[key]?.map(val => {
      return {
        key: uniqueId(),
        name: key,
        label: val,
      };
    });
  };

  useEffect(() => {
    (async () => {
      if (fetchAttributes) {
        setBusy(true);
        await retrieveAttributes();
        setBusy(false);
      }
    })();
  }, []);

  const debouncedLeadsSearch = useCallback(
    () => debounce(() => searchLeads(filterParams), 300),
    [filterParams, searchLeads],
  );

  const previousDebouncedSearch = usePrevious(debouncedLeadsSearch());

  useEffect(() => {
    previousDebouncedSearch?.cancel();
  }, [previousDebouncedSearch]);

  useEffect(() => {
    if (filterParams?.query?.length || filterParams?.exclude?.length) {
      debouncedLeadsSearch()();
    }
    if (!filterParams?.query?.length) {
      resetLeads();
    }
  }, [filterParams, debouncedLeadsSearch, resetLeads]);

  return {
    busy,
    mapAttributeValuesToCheckboxItems,
  };
};
